import React from 'react';
import Chevron from '../user-dashboard/utility/Chevron';

function Pagination(props) {
  /* ========
    Leaned heavily on this guide for help with pagination styling/logic on FE:
    https://www.digitalocean.com/community/tutorials/how-to-build-custom-pagination-with-react
  ======== */

  const { resultPages, currentPage, handlePageChange, primaryColor } = {
    ...props,
  };

  const LEFT_PAGE = 'LEFT';
  const RIGHT_PAGE = 'RIGHT';

  const range = (from, to, step = 1) => {
    let i = from;
    const rangeArr = [];

    while (i <= to) {
      rangeArr.push(i);
      i += step;
    }

    return rangeArr;
  };

  const selected = {
    backgroundColor: primaryColor,
    color: 'white',
  };

  const hidden = {
    visibility: 'hidden',
  };

  const fetchPageNumbers = () => {
    const pageNeighbours = 1;
    /**
     * totalNumbers: the total page numbers to show on the control
     * totalBlocks: totalNumbers + 2 to cover for the left(<) and right(>) controls
     */
    const totalNumbers = pageNeighbours * 2 + 3;
    const totalBlocks = totalNumbers + 2;

    if (resultPages > totalBlocks) {
      const startPage = Math.max(2, currentPage - pageNeighbours);
      const endPage = Math.min(resultPages - 1, currentPage + pageNeighbours);

      let pages = range(startPage, endPage);

      /**
       * hasLeftSpill: has hidden pages to the left
       * hasRightSpill: has hidden pages to the right
       * spillOffset: number of hidden pages either to the left or to the right
       */
      const hasLeftSpill = startPage > 2;
      const hasRightSpill = resultPages - endPage > 1;
      const spillOffset = totalNumbers - (pages.length + 1);

      switch (true) {
        // handle: (1) < {5 6} [7] {8 9} (10)
        case hasLeftSpill && !hasRightSpill: {
          const extraPages = range(startPage - spillOffset, startPage - 1);
          pages = [LEFT_PAGE, ...extraPages, ...pages];
          break;
        }

        // handle: (1) {2 3} [4] {5 6} > (10)
        case !hasLeftSpill && hasRightSpill: {
          const extraPages = range(endPage + 1, endPage + spillOffset);
          pages = [...pages, ...extraPages, RIGHT_PAGE];
          break;
        }

        // handle: (1) < {4 5} [6] {7 8} > (10)
        case hasLeftSpill && hasRightSpill:
        default: {
          pages = [LEFT_PAGE, ...pages, RIGHT_PAGE];
          break;
        }
      }

      return [1, ...pages, resultPages];
    }

    return range(1, resultPages);
  };

  const displayPageNumbers = () => {
    if (!resultPages || resultPages === 1) return null;

    const pages = fetchPageNumbers();
    return (
      <>
        <button
          type="button"
          tabIndex={0}
          style={currentPage === 1 ? hidden : null}
          className="user-results-pages__page-button"
          onClick={() => {
            handlePageChange(currentPage - 1);
          }}
          onKeyDown={(e) => {
            if (['Enter'].includes(e.key)) handlePageChange(currentPage - 1);
          }}
        >
          <Chevron
            color="black"
            transform="rotate(180deg)"
            marginLeft={0}
            height="10px"
            ariaLabel="Click to move backward one page"
          />
          Prev
        </button>
        {pages.map((page) => {
          if (page === LEFT_PAGE || page === RIGHT_PAGE)
            return <div key={page}>...</div>;

          return (
            <button
              key={page}
              tabIndex={0}
              style={currentPage === page ? selected : null}
              className="user-results-pages__page-button"
              type="button"
              onClick={() => {
                handlePageChange(page);
              }}
              onKeyDown={(e) => {
                if (['Enter'].includes(e.key)) handlePageChange(page);
              }}
            >
              {page}
            </button>
          );
        })}
        <button
          style={currentPage === resultPages ? hidden : null}
          tabIndex={0}
          className="user-results-pages__page-button"
          type="button"
          onClick={() => {
            handlePageChange(currentPage + 1);
          }}
          onKeyDown={(e) => {
            if (['Enter'].includes(e.key)) handlePageChange(currentPage + 1);
          }}
        >
          Next
          <Chevron
            color="black"
            marginLeft="10px"
            marginRight={0}
            height="10px"
            ariaLabel="Click to move forward one page"
          />
        </button>
      </>
    );
  };

  return (
    <div id="user-results-pages" className="user-results-pages">
      {resultPages > 1 && displayPageNumbers()}
    </div>
  );
}

export default Pagination;
